/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useMemo, useState} from "react"
import styles from "./StatsEnrollment.module.css"
import {handleError} from "helpers"
import {admissionService} from "services"
import {EnrollmentStats} from "types/admissions"
import {Spin} from "antd"
import moment from "moment"
import cx from "classnames"
import {Icon} from "components"

type Props = {
    form: any
    setState: any
    departmentId: number
}

type RowData = {
    id: number
    name: string
    level: 1 | 2 | 3
    children: RowData[]
    data: {
        termId?: number
        total: number
    }[]
    reportParams: {
        campusId: number
        termId?: number
        programId?: number
        phaseId?: number
    }
}

// Utility function to convert stats data to RowData[] format
function convertStatsToRowData(stats: EnrollmentStats): RowData[] {
    return stats.campuses.map((campus) => {
        const campusData = stats.data.filter((x) => x.campusId === campus.campusId)
        const programIds = new Set(campusData.map((x) => x.programs.map((y) => y.programId)).flat())

        return {
            id: campus.campusId,
            name: campus.campusName,
            level: 1,
            children: Array.from(programIds).map((programId) => {
                const programData = campusData
                    .map((x) => x.programs)
                    .flat()
                    .filter((x) => x.programId === programId)
                const phaseIds = new Set(programData.map((x) => x.phases.map((y) => y.phaseId)).flat())

                return {
                    id: programId,
                    name: programData[0].programName,
                    level: 2,
                    children: Array.from(phaseIds).map((phaseId) => {
                        const phaseData = programData
                            .map((x) => x.phases)
                            .flat()
                            .filter((x) => x.phaseId === phaseId)
                        return {
                            id: phaseId,
                            name: phaseData[0].phaseName,
                            level: 3,
                            children: [],
                            data: phaseData.map((x) => ({
                                termId: x.termId,
                                total: x.total
                            })),
                            reportParams: {
                                campusId: campus.campusId,
                                programId: programId,
                                phaseId: phaseId
                            }
                        }
                    }),
                    data: programData.map((x) => ({
                        termId: x.termId,
                        total: x.total
                    })),
                    reportParams: {
                        campusId: campus.campusId,
                        programId: programId
                    }
                }
            }),
            data: campusData.map((x) => ({
                termId: x.termId,
                total: x.total
            })),
            reportParams: {
                campusId: campus.campusId
            }
        }
    })
}

export function StatsEnrollment(props: Props) {
    const {form, setState, departmentId} = props
    const [stats, setStats] = useState<EnrollmentStats>({
        campuses: [],
        terms: [],
        data: []
    })
    const [isLoading, setIsLoading] = useState(false)
    const [additionalParams, setAdditionalParams] = useState<{
        dateRange?: [string, string]
        advisorProfileIds?: number[]
    }>({})

    useEffect(() => {
        if (form.applySearch) {
            getStatsInfo()
            setState({applySearch: false})
        }
    }, [form])

    useEffect(() => {
        getStatsInfo()
    }, [])

    function getFilter(form) {
        const {campus, program, advisor, term, dateRange} = form
        let filter: any = {}
        if (term) {
            filter.termsIds = term.map(({id}) => id)
        }
        if (campus) {
            filter.campusIds = campus.map(({id}) => id)
        }
        if (program) {
            filter.programIds = program.map(({id}) => id)
        }
        if (advisor) {
            filter.advisorProfileIds = (advisor || []).map((item) => item?.profileId).filter((item) => item)
        }
        if (dateRange) {
            filter.dateRange = [
                moment(dateRange[0]).startOf("date").toISOString(),
                moment(dateRange[1]).endOf("date").toISOString()
            ]
        }
        setAdditionalParams({
            dateRange: filter.dateRange,
            advisorProfileIds: filter.advisorProfileIds
        })

        return filter
    }

    async function getStatsInfo() {
        try {
            setIsLoading(true)
            const filter = getFilter(props.form)
            const data = await admissionService.getEnrollmentStats({
                departmentId,
                advisorProfileIds: [],
                ...filter
            })

            setStats(data)
        } catch (error) {
            handleError(error)
        } finally {
            setIsLoading(false)
        }
    }

    const rows = useMemo((): RowData[] => {
        return convertStatsToRowData(stats)
    }, [stats])

    return (
        <div className={styles.wrap}>
            <p className={styles.title}>Admissions Enrollment</p>
            {isLoading ? (
                <Spin className={styles.loading} />
            ) : (
                <div className={styles.body}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th className={styles.tableHeaderText} />
                                {stats.terms.map(({termName}, index) => {
                                    return (
                                        <th key={index} className={styles.tableHeaderText}>
                                            {termName || "Unknown"}
                                        </th>
                                    )
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => {
                                return (
                                    <TableCell
                                        key={index}
                                        row={row}
                                        terms={stats.terms}
                                        additionalParams={additionalParams}
                                    />
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}

interface TableCellProps {
    row: RowData
    terms: {
        termId?: number
        termName: string
    }[]
    additionalParams: {
        dateRange?: [string, string]
        advisorProfileIds?: number[]
    }
}

function TableCell(props: TableCellProps) {
    const {row, terms, additionalParams} = props
    const canExpand = row.children.length > 0
    const [expanded, setExpanded] = useState(false)

    const gotoReport = (termId?: number) => {
        console.log({termId, reportParams: row.reportParams, additionalParams})
    }

    return (
        <>
            <tr>
                <td className={styles.tableColumn}>
                    <div
                        className={cx(styles.rowHeader, {[styles.noExpandable]: !canExpand})}
                        onClick={() => canExpand && setExpanded(!expanded)}>
                        <span
                            className={cx(styles.rowHeaderText, {
                                [styles.rowHeaderLevel2]: row.level === 2,
                                [styles.rowHeaderLevel3]: row.level === 3
                            })}>
                            {row.name}
                        </span>
                        {canExpand && (
                            <button className={cx(styles.collapseButton, {[styles.collapsed]: expanded})}>
                                <Icon icon="ARROW_DOWN" color="#666666" className={styles.headingIcon} />
                            </button>
                        )}
                    </div>
                </td>
                {terms.map(({termId}, index) => {
                    const total = row.data.find((x) => x.termId === termId)?.total
                    const isNavigable = false //total > 0

                    return (
                        <td
                            key={index}
                            className={cx(styles.tableCell, {[styles.navigable]: isNavigable})}
                            onClick={() => isNavigable && gotoReport(termId)}>
                            {total}
                        </td>
                    )
                })}
            </tr>
            {expanded &&
                row.children.map((child, index) => {
                    return <TableCell key={index} row={child} terms={terms} additionalParams={additionalParams} />
                })}
        </>
    )
}
